/**
 * What the SPA knows about authentication: almost nothing, on purpose.
 *
 * ADR-005 / SALVAGE §4 D1 retired msal. There is no client id, no tenant id,
 * no authority and no token in the browser — so there is nothing to bake in at
 * build time and nothing to get wrong with a build arg. The server owns the
 * whole flow; the SPA follows a redirect and carries an httpOnly cookie it
 * cannot read.
 */

/** Mirrors the server's AUTH_MODE. Discovered at runtime, never compiled in. */
export type AuthMode = 'entra' | 'google' | 'dev';

/** What `GET /api/auth/config-check` tells an anonymous caller. */
export interface AuthConfig {
  authMode: AuthMode;
  provider: string | null;
  allowedDomains: string[];
  ok: boolean;
  problems: string[];
}

export const DEFAULT_AUTH_CONFIG: AuthConfig = {
  authMode: 'entra',
  provider: 'entra',
  allowedDomains: [],
  ok: false,
  problems: ['The server did not answer /api/auth/config-check.'],
};

/** Narrow an unknown JSON body into an AuthConfig without trusting any of it. */
export function readAuthConfig(raw: unknown): AuthConfig {
  const o = (raw ?? {}) as Record<string, unknown>;
  const mode = o.authMode;
  return {
    authMode: mode === 'dev' || mode === 'google' || mode === 'entra' ? mode : 'entra',
    provider: typeof o.provider === 'string' ? o.provider : null,
    allowedDomains: Array.isArray(o.allowedDomains) ? o.allowedDomains.map(String) : [],
    ok: o.ok === true,
    problems: Array.isArray(o.problems) ? o.problems.map(String) : [],
  };
}

/**
 * Where the sign-in button goes.
 *
 * `next` is a same-site path, and the server re-checks it — the guard lives
 * there because a redirect target the browser proposes is untrusted input.
 */
export function loginHref(next: string): string {
  const safe = next.startsWith('/') && !next.startsWith('//') ? next : '/';
  return `/api/auth/login?next=${encodeURIComponent(safe)}`;
}

/** The label on the button, per provider. */
export function signInLabel(mode: AuthMode): string {
  if (mode === 'google') return 'Sign in with Google';
  if (mode === 'entra') return 'Sign in with Microsoft';
  return 'Sign in';
}

export const DEV_USER_STORAGE_KEY = 'cigs-dev-user';
